import { inject, Injectable } from '@angular/core';
import { Store } from '@sama/app-state';
import { assert, isDefined } from '@sama/js-core';
import { InjectableSuperclass } from '@sama/ng-core';
import { forEach, isUndefined, omitBy } from 'lodash';
import { pairwise } from 'rxjs';
import { TaskWorkspaceStore } from '../../../../core/h3ml/state/task-workspace-store';
import { getAttributeHelper } from '../../../../core/shared/attribute-helper/get-attribute-helper';
import { ShapeService } from '../../../../core/shared/misc-services/shape.service';
import { AnnotationState } from '../../../../cross-team-shared/annotation/shared/state/annotation-state';
import {
  Tag,
  TagsState,
} from '../../../../cross-team-shared/annotation/shared/state/shape-state';
import { ensureKeyDynamicTag } from '../../../../cross-team-shared/annotation/shared/utils/keyframe-utils';
import { getDynamicTag } from '../../../../cross-team-shared/misc-services/tag-helper';
import { Attributes } from '../../../../team-apis/core/taxonomy';
import { Attribute } from '../../../../team-apis/otterdam/taxonomy';
import { AvailableAttributeService } from './available-attribute.service';

@Injectable()
export class DefaultAttributeService extends InjectableSuperclass {
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private availableAttributeService = inject(AvailableAttributeService);
  private shapeService = inject(ShapeService);

  startListening() {
    this.subscribeToObjects();
  }

  refreshAttributes(annotationStore: Store<AnnotationState>) {
    const attributes = this.availableAttributeService.getAttributes(
      annotationStore.state(),
    );
    if (isUndefined(attributes)) {
      return;
    }
    this.removeAttributesByClass(annotationStore, attributes);
    this.setAttributeOptions(annotationStore, attributes);
  }

  private subscribeToObjects() {
    this.subscribeTo(
      this.taskWorkspaceStore('objects').$.pipe(pairwise()),
      ([previousObjects, currentObjects]) => {
        forEach(currentObjects, (currentObject, key) => {
          const previousObject = previousObjects[Number(key)];

          // ignore newly created objects (those are created without class)
          // or pre annotations
          if (isUndefined(previousObject)) {
            return;
          }

          assert(isDefined(currentObject), 'object state must be defined');

          if (previousObject.class !== currentObject.class) {
            this.setDefaultAttributes(Number(key));
          }
        });
      },
    );
  }

  private setDefaultAttributes(objectId: number) {
    const objectStore = this.taskWorkspaceStore.getObjectStore(objectId);
    const shapeStores = this.shapeService.getShapeStores(
      objectStore.state().childShapeIds,
    );
    const storesToUpdate = [objectStore, ...shapeStores];

    storesToUpdate.forEach((annotationStore) => {
      this.refreshAttributes(annotationStore);
    });
  }

  private setAttributeOptions(
    annotationStore: Store<AnnotationState>,
    attributes: Attributes,
  ) {
    for (const [name, attribute] of Object.entries(attributes)) {
      assert(attribute, `attribute ${name} not defined`);
      const attributeType = attribute.__type;

      const defaultValue =
        getAttributeHelper(attributeType).getDefaultOptionValue(attribute);

      if (attribute.isDynamic) {
        this.setDynamicAttributeWithoutOverwrite(
          annotationStore,
          attribute,
          defaultValue,
        );
      } else {
        this.setStaticAttributeWithoutOverwrite(
          annotationStore,
          attribute,
          defaultValue,
        );
      }
    }
  }

  private setStaticAttributeWithoutOverwrite(
    tagsStore: Store<TagsState>,
    attribute: Attribute,
    attributeValue: Tag,
  ) {
    tagsStore('tags')(attribute.name).setUsing(
      (value) => value ?? attributeValue,
    );
  }

  private setDynamicAttributeWithoutOverwrite(
    tagsStore: Store<TagsState>,
    attribute: Attribute,
    attributeValue: Tag,
  ) {
    const frameNumber = this.taskWorkspaceStore.getFirstFusion().frameNumber;

    const frameSpecificTags = tagsStore.state().dynamic_tags[attribute.name];
    const dynamicTag = getDynamicTag(frameSpecificTags, frameNumber);

    if (isUndefined(dynamicTag)) {
      ensureKeyDynamicTag(tagsStore, attribute.name, {
        frame_number: frameNumber,
        value: attributeValue,
      });
    }
  }

  private removeAttributesByClass(
    annotationStore: Store<TagsState>,
    attributes: Attributes,
  ) {
    const attributeKeys = Object.keys(attributes);

    annotationStore('tags').setUsing((tags) =>
      omitBy(tags, (_, key) => !attributeKeys.includes(key)),
    );

    annotationStore('dynamic_tags').setUsing((dynamicTags) =>
      omitBy(dynamicTags, (_, key) => !attributeKeys.includes(key)),
    );
  }
}
